import {
    type ITagModelObject,
    PermissionType,
} from '@experiences/interfaces';
import {
    ApDataGridColumn,
    ApDataGridFooter,
    ApDataGridHeader,
    ApDataGridHeaderButton,
    ApDataGridRowActions,
    ApDataGridRowButton,
    ApDataGridWrapper,
} from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { isAdminSelector } from '../../../../store/selectors';
import { hasPermission } from '../../addedit/properties/TenantTagsUtil';
import { useTagsDisabled } from '../../common/useTagsDisabled';
import useTenantTagPropertiesViewModel from './TenantTagPropertiesViewModel';
import TenantTagsPropertiesMoreActions from './TenantTagsPropertiesMoreActions';

const TenantTagsPropertiesComponent: React.FC = () => {
    const { formatMessage: translate } = useIntl();
    const { checkDisabled } = useTagsDisabled();
    const {
        openDeleteDialog, tenantId, permissions, loading, refresh: refreshProperties, handleAddProperty, handleDeleteProperty, propertiesData, onChangePagination,
    } = useTenantTagPropertiesViewModel();
    const isAdmin = useSelector(isAdminSelector);

    return (
        <div
            style={{ width: '100%' }}
            data-cy='tenant-tags-properties-grid-container'>
            <ApDataGridWrapper<ITagModelObject>
                data={propertiesData?.results ?? []}
                initialFilters={{
                    sort: [
                        {
                            field: 'name',
                            direction: 'asc',
                            title: translate({ id: 'CLIENT_KEY' }),
                        },
                    ],
                }}
                loading={loading}
                refreshable
                refresh={refreshProperties}
                selectable={hasPermission(permissions, [ PermissionType.View, PermissionType.Delete ])}
                toggleColumns
                onChange={onChangePagination}
                dataCy='tenant-tags-properties-grid'
            >
                <ApDataGridHeader<ITagModelObject> search>
                    <ApDataGridHeaderButton<ITagModelObject>
                        id='deleteProperties'
                        key='deleteProperties'
                        type='action'
                        buttonType='mat-flat-button'
                        color="warn"
                        text={translate({ id: 'CLIENT_DELETE' })}
                        label={translate({ id: 'CLIENT_DELETE' })}
                        icon='delete'
                        onClick={handleDeleteProperty}
                        dataCy='delete-property-button'
                        disabled={checkDisabled(permissions, [ PermissionType.View, PermissionType.Delete ])}
                        visible={isAdmin}
                    />
                    <ApDataGridHeaderButton<ITagModelObject>
                        id='addProperty'
                        key='addProperty'
                        type='main'
                        buttonType='mat-flat-button'
                        color='primary'
                        text={translate({ id: 'CLIENT_ADD_PROPERTY' })}
                        label={translate({ id: 'CLIENT_ADD_PROPERTY' })}
                        onClick={handleAddProperty}
                        dataCy='add-property-button'
                        disabled={checkDisabled(permissions, [ PermissionType.View, PermissionType.Create ])}
                        visible={isAdmin}
                    />
                </ApDataGridHeader>
                <ApDataGridColumn<ITagModelObject>
                    property='name'
                    sort='asc'
                    title={translate({ id: 'CLIENT_KEY' })}
                    searchable
                />
                <ApDataGridColumn<ITagModelObject>
                    property='dataType'
                    title={translate({ id: 'CLIENT_VALUE_TYPE' })}
                    searchable
                />
                <ApDataGridColumn<ITagModelObject>
                    property='values'
                    title={translate({ id: 'CLIENT_VALUES' })}
                    searchable
                />
                <ApDataGridColumn<ITagModelObject>
                    property='description'
                    title={translate({ id: 'CLIENT_DESCRIPTION' })}
                    searchable
                />
                <ApDataGridColumn<ITagModelObject>
                    property='referencesCount'
                    title={translate({ id: 'CLIENT_TAGGED_OBJECTS' })}
                    searchable
                />
                {isAdmin && (
                    <ApDataGridRowActions>
                        <ApDataGridRowButton<ITagModelObject>
                            id='showMoreActions'
                            label={translate({ id: 'CLIENT_SHOW_MORE_ACTIONS' })}
                            icon='more_vert'
                            render={(row) => <TenantTagsPropertiesMoreActions
                                row={row}
                                tenantId={tenantId}
                                permissions={permissions}
                                onDelete={openDeleteDialog} />}
                        />
                    </ApDataGridRowActions>
                )}
                <ApDataGridFooter
                    length={propertiesData?.totalCount ?? 0}
                    pageSizes={[ 5, 10, 25, 50 ]}
                />
            </ApDataGridWrapper>
        </div>
    );
};

export default TenantTagsPropertiesComponent;
